import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Drawer } from 'antd';
import useTranslation from '@src/hooks/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { reactLocalStorage } from '@src/utils/localStorage';
import { useRouter } from 'next/router';
import _ from 'lodash';
import useMediaQuery from '@src/hooks/useMediaQuery';

import {
	getCategories, getHierarchy, getSearch, getSearchState, getSearchPagination,
	getCommodityPublishedType,
} from '@src/store/selectors/category';
import { ArrowLeftTailIcon } from '@src/components/common/icon';
import { getCommoditySearchSwitchCall, getCommodityPublishedTypeCall } from '@src/store/actions/category';
import { getTagsCall } from '@src/store/actions/event';
import { getTeachersCall } from '@src/store/actions/school';
import { TSideEffectState } from '@src/types/common';
import { STATE_LOADING } from '@src/const/common';
import { ICategory } from '@src/types/category';

import Breadcrumb from './breadcrumb';
import CoverBanner from './coverBanner';
import Menu from './menu';
import List from './list';
import './global.module.less';

const CategoryPageBackground = styled.div`
	background: #FFFFFF;
	display: flex;
	flex-direction: column;
	align-items: center;
	@media ${props => props.theme.device.mobile} {
		display: unset;
		padding-top: 52px;
	}
`;

const Content = styled.div`
	display: flex;
	flex-direction: row;
	padding: 0 40px 0 28px;
	width: 100%;
	@media ${props => props.theme.device.lgDesktop} {
		max-width: 1200px;
	}
	@media 
		${props => props.theme.device.mobile},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet}
		{
			padding: 0 20px;
		}
`;

const WebMenu = styled(Menu)`
	@media 
		${props => props.theme.device.mobile},
		${props => props.theme.device.smTablet},
		${props => props.theme.device.tablet}
		{
			display: none;
		}
`;

const MobileMenu = styled(Menu)`
	width: 100% !important;
	& .menu {
		width: 100% !important;
	}
	& .wrap {
		width: 100% !important;
	}
	& .option {
		padding: 0px 12px;
	}
`;

const TitleContent = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	font-size: 18px;
	color: #101010;
	position: relative;
`;

const IconContent = styled.div`
	cursor: pointer;
	position: absolute;
	top: -2px;
	left: 0;
`;

const StyledDrawer = styled(Drawer)`
	&.ant-drawer .ant-drawer-body {
		padding: unset !important;
	}
	.ant-drawer-header {
		display: block;
	}
	.ant-drawer-title {
		width: 100%;
	}
`;

// powder by https://stackoverflow.com/questions/53658638/finding-a-path-to-target-object-in-nested-array-of-objects-in-javascript
const buildFullTree = (departmentTree, category, data = []) => {
	// eslint-disable-next-line no-shadow
	const findPath = (node, category) => {
		if (node.id === category.id) {
			return [node];
		} else {
			const children = node?.children || [];
			// eslint-disable-next-line no-restricted-syntax
			for (const child of children) {
				const childPath = findPath(child, category);
				if (Array.isArray(childPath)) {
					childPath.unshift(child);
					return childPath;
				}
			}
		}
	};

	const foundPath = findPath(departmentTree, category);
	if (Array.isArray(foundPath)) {
		data.push(departmentTree);
		data.push(...foundPath);
	}
	return data;
};

const CategoryPage = (): JSX.Element => {
	const dispatch = useDispatch();
	const router = useRouter();
	const { shortLink, category_short_link, keyword = null, tag_ids } = router.query;
	const menuRef = useRef();
	const mobileMenuRef = useRef();
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);
	const [pageNumber, setPageNumber] = useState(0);
	const [pageSize, setPageSize] = useState(15);
	const [searchKeyword, setSearchKeyword] = useState(keyword as string | null);
	const [pageFilter, setFilter] = useState({ page: 0, page_size: 15 } as any);
	const [link, selectLink] = useState(shortLink);
	const [sortType, setSortType] = useState('' as 'latest' | 'oldest' | '');

	const categories = useSelector(getCategories);
	const hierarchy = useSelector(getHierarchy);
	const search = useSelector(getSearch);
	const { totalCount } = useSelector(getSearchPagination);
	const searchState = useSelector(getSearchState) as TSideEffectState;
	const publishedType = useSelector(getCommodityPublishedType);
	const [category, setCategory] = useState({} as ICategory);
	const breadcrumbPath = buildFullTree(
		{ id: '', children: hierarchy },
		{ id: category?.id },
	).slice(0, -1);

	useEffect(() => {
		setCategory(_.find(categories, { short_link: shortLink }));
	}, [categories]);

	const bannerCoverLink = category?.banner_link?.link;
	useEffect(() => {
		dispatch(getTagsCall({ in_use_only: true, school_id: reactLocalStorage.get('schoolId') }));
		dispatch(getTeachersCall({ from_front_desk: true }));
		dispatch(getCommodityPublishedTypeCall({}));
	}, []);

	const openMenu = () => setVisible(true);

	const onFilterChange = filter => {
		const query = {
			page: filter.page || 0,
			page_size: pageSize,
			...searchKeyword ? { keyword: searchKeyword } : {},
			...sortType ? { sort_type: sortType } : {},
			tag_ids,
			...filter,
			category_short_link:
				filter.category_short_link ||
				pageFilter.category_short_link ||
				category_short_link || shortLink || 'all',
		};
		setFilter(query);
		selectLink(query.category_short_link);
		dispatch(getCommoditySearchSwitchCall(query));
		const selectCategory = _.find(categories, { short_link: query.category_short_link });
		setCategory(selectCategory);
		if (link !== query.category_short_link) {
			window.history.pushState({}, selectCategory?.name, `/category/${query.category_short_link}`);
		}
		if (!_.isNumber(filter.page)) {
			setPageNumber(0);
		}
	};

	useEffect(() => {
		if (sortType || _.isString(searchKeyword)) {
			setPageNumber(0);
		}
	}, [sortType, searchKeyword]);

	useEffect(() => {
		setPageNumber(0);
	}, [link]);

	useEffect(() => {
		const query = {
			...pageFilter,
			...searchKeyword ? { keyword: searchKeyword } : {},
			...sortType ? { sort_type: sortType } : {},
		};
		delete query.page;
		onFilterChange(query);
	}, [sortType, pageSize]);

	useEffect(() => {
		const query = {
			...pageFilter,
			...searchKeyword ? { keyword: searchKeyword } : {},
			...sortType ? { sort_type: sortType } : {},
			page: pageNumber,
		};
		onFilterChange(query);
	}, [pageNumber]);

	const { mediaMatch } = useMediaQuery();
	const { isTablet, isSmTablet, isMobile } = mediaMatch;
	const resetFilter = () => {
		if (isTablet || isSmTablet || isMobile) {
			if (mobileMenuRef?.current) {
				setSearchKeyword('');
				/* eslint-disable @typescript-eslint/ban-ts-comment */
				// @ts-ignore
				mobileMenuRef?.current.reset();
				setPageNumber(0);
			}
		} else if (menuRef?.current) {
			setSearchKeyword('');
			/* eslint-disable @typescript-eslint/ban-ts-comment */
			// @ts-ignore
			menuRef?.current.reset();
			setPageNumber(0);
		}
	};

	return (
		<CategoryPageBackground>
			<Breadcrumb path={breadcrumbPath} onFilterChange={onFilterChange} />
			{ bannerCoverLink && <CoverBanner imageUrl={bannerCoverLink} /> }
			<Content>
				<WebMenu
					ref={menuRef}
					categories={categories}
					hierarchy={hierarchy}
					onFilterChange={onFilterChange}
					setSearchKeyword={setSearchKeyword}
					breadcrumbPath={breadcrumbPath}
					setSortType={setSortType}
					publishedType={publishedType}
					resetFilter={resetFilter}
				/>
				<StyledDrawer
					title={(
						<TitleContent>
							<IconContent onClick={() => setVisible(false)}>
								<ArrowLeftTailIcon />
							</IconContent>
							{t('category:filter')}
						</TitleContent>
					)}
					placement="right"
					width="100%"
					onClose={() => setVisible(false)}
					visible={visible}
					closable={false}
				>
					<MobileMenu
						ref={mobileMenuRef}
						categories={categories}
						hierarchy={hierarchy}
						onFilterChange={onFilterChange}
						setSearchKeyword={setSearchKeyword}
						breadcrumbPath={breadcrumbPath}
						setSortType={setSortType}
						visible={visible}
						setVisible={setVisible}
						publishedType={publishedType}
						resetFilter={resetFilter}
					/>
				</StyledDrawer>
				<List
					openMenu={openMenu}
					list={search}
					category={category}
					setPageNumber={setPageNumber}
					pageNumber={pageNumber}
					setPageSize={setPageSize}
					pageSize={pageSize}
					searchKeyword={searchKeyword}
					setSearchKeyword={setSearchKeyword}
					sortType={sortType}
					setSortType={setSortType}
					isLoading={searchState === STATE_LOADING}
					resetFilter={resetFilter}
					totalCount={totalCount}
					onFilterChange={onFilterChange}
				/>
			</Content>
		</CategoryPageBackground>
	);
};

export default CategoryPage;
